<template>
  <div class="sgm-page">
    <header class="sgm-header">
      <div>
        <div class="eyebrow">SGM</div>
        <h2>SGM综合菜单</h2>
        <p>{{ userData.username || 'User' }} / {{ userData.job || 'SGM' }} / {{ userData.webstatus || 'VIP' }}</p>
      </div>
      <div class="server-switch">
        <div class="server-switch-title">数据来源</div>
        <el-radio-group
          v-model="selectedServer"
          size="small"
          :disabled="loading || snBusy"
          @change="handleServerChange"
        >
          <el-radio-button
            v-for="item in serverOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
        <div class="server-switch-host">{{ currentServer.host }}</div>
      </div>
      <div class="header-actions">
        <el-tag type="success" effect="plain">SGM专用</el-tag>
        <el-button :icon="Refresh" :loading="loading || currentSnState.loading" :disabled="snBusy || loading" circle @click="activeSection === 'files' ? loadTree() : loadCurrentSnRecords()" />
      </div>
    </header>

    <section class="model-band">
      <span
        v-for="model in models"
        :key="model"
        class="model-chip"
      >
        {{ model }}
      </span>
    </section>

    <el-radio-group v-model="activeSection" class="section-switch" :disabled="snBusy" @change="handleSectionChange">
      <el-radio-button label="files">文件管理</el-radio-button>
      <el-radio-button label="forbidden">SN 封禁管理</el-radio-button>
      <el-radio-button label="counts">安全校验次数</el-radio-button>
    </el-radio-group>

    <section v-if="activeSection !== 'files'" class="sn-panel workspace-card">
      <div class="pane-title">
        <strong>{{ activeSection === 'forbidden' ? 'SN 封禁管理' : '安全校验次数管理' }}</strong>
        <span>共 {{ currentSnState.total }} 条记录</span>
      </div>
      <el-form class="sn-filters" :inline="true" @submit.prevent="searchSnRecords">
        <el-form-item label="SN">
          <el-input v-model="currentSnState.sn" placeholder="输入完整 SN，留空查询全部" maxlength="256" clearable :disabled="snBusy" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" native-type="submit" :loading="currentSnState.loading" :disabled="snBusy && !currentSnState.loading">查询</el-button>
          <el-button :disabled="snBusy" @click="resetSnFilters">重置</el-button>
        </el-form-item>
      </el-form>
      <el-alert v-if="currentSnState.error" class="sn-error" :title="currentSnState.error" type="error" :closable="false" show-icon />
      <el-table v-loading="currentSnState.loading" :data="currentSnState.items" border empty-text="暂无符合条件的记录">
        <el-table-column prop="sn" label="SN" min-width="180" />
        <template v-if="activeSection === 'forbidden'">
          <el-table-column label="封禁日期" width="140">
            <template #default="{ row }">{{ formatSnDate(row.forbidden_date) }}</template>
          </el-table-column>
          <el-table-column prop="reason" label="封禁原因" min-width="220" show-overflow-tooltip />
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button type="danger" link :disabled="snBusy" @click="handleUnbanSn(row)">解封</el-button>
            </template>
          </el-table-column>
        </template>
        <template v-else>
          <el-table-column label="日期" width="140">
            <template #default="{ row }">{{ formatSnDate(row.query_date) }}</template>
          </el-table-column>
          <el-table-column label="安全校验次数" min-width="140">
            <template #default="{ row }">{{ row.cnt ?? '-' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" link :disabled="snBusy" @click="openCountEditor(row)">修改</el-button>
            </template>
          </el-table-column>
        </template>
      </el-table>
      <div class="sn-pagination">
        <el-pagination v-model:current-page="currentSnState.page" :page-size="20" :total="currentSnState.total" :disabled="snBusy" layout="prev, pager, next" @current-change="loadCurrentSnRecords" />
      </div>
    </section>

    <el-dialog v-model="countEditVisible" title="修改安全校验次数" width="420px" class="sn-count-dialog" :close-on-click-modal="false" :close-on-press-escape="!countSaving" :show-close="!countSaving" :before-close="closeCountEditor">
      <el-form label-width="90px" @submit.prevent="saveSnCount">
        <el-form-item label="SN">{{ countEditor.sn }}</el-form-item>
        <el-form-item label="日期">{{ formatSnDate(countEditor.query_date) }}</el-form-item>
        <el-form-item label="原次数">{{ countEditor.expected_cnt ?? '-' }}</el-form-item>
        <el-form-item label="新次数">
          <el-input-number v-model="countEditor.cnt" :min="0" :max="2147483647" :step="1" step-strictly :disabled="countSaving" />
        </el-form-item>
        <el-alert v-if="countEditError" :title="countEditError" type="error" :closable="false" show-icon />
      </el-form>
      <template #footer>
        <el-button :disabled="countSaving" @click="countEditVisible = false">取消</el-button>
        <el-button type="primary" :loading="countSaving" @click="saveSnCount">保存</el-button>
      </template>
    </el-dialog>

    <main v-show="activeSection === 'files'" class="sgm-workspace">
      <section class="tree-pane">
        <div class="pane-title">
          <strong>C:\sgm</strong>
          <span>{{ flatFileCount }} 个文件</span>
        </div>
        <el-scrollbar class="tree-scroll">
          <el-empty v-if="!loading && fileTree.length === 0" description="暂无文件" />
          <el-tree
            ref="treeRef"
            v-else
            v-loading="loading"
            :data="fileTree"
            node-key="relativePath"
            :props="treeProps"
            lazy
            :load="loadTreeNode"
            :expand-on-click-node="false"
            @node-click="handleNodeClick"
          >
            <template #default="{ data }">
              <span class="tree-node" :class="{ active: selectedNode?.relativePath === data.relativePath }">
                <el-icon>
                  <FolderOpened v-if="data.isDirectory" />
                  <Document v-else />
                </el-icon>
                <span class="tree-label">{{ data.name }}</span>
              </span>
            </template>
          </el-tree>
        </el-scrollbar>
      </section>

      <section class="detail-pane">
        <el-empty v-if="!selectedNode" description="请选择文件" />
        <template v-else>
          <div class="detail-head">
            <div>
              <div class="eyebrow">{{ selectedNode.isDirectory ? '目录' : fileTypeLabel(selectedNode.fileType) }}</div>
              <h3>{{ selectedNode.name }}</h3>
              <p>{{ selectedNode.relativePath || '.' }}</p>
            </div>
            <el-tag :type="fileTagType(selectedNode.fileType)" effect="plain">
              {{ fileTypeLabel(selectedNode.fileType) }}
            </el-tag>
          </div>

          <dl class="meta-grid">
            <div>
              <dt>文件大小</dt>
              <dd>{{ selectedNode.isDirectory ? '-' : formatSize(selectedNode.size) }}</dd>
            </div>
            <div>
              <dt>最近修改</dt>
              <dd>{{ selectedNode.modifyTime || '-' }}</dd>
            </div>
            <div>
              <dt>可查看</dt>
              <dd>{{ selectedNode.allowPreview ? '是' : '否' }}</dd>
            </div>
            <div>
              <dt>可替换</dt>
              <dd>{{ selectedNode.allowUpload ? '是' : '否' }}</dd>
            </div>
            <div>
              <dt>可运行</dt>
              <dd>{{ selectedNode.allowRun ? '是' : '否' }}</dd>
            </div>
          </dl>

          <el-alert
            v-if="selectedNode.fileType === 'exe'"
            class="info-alert"
            type="warning"
            :closable="false"
            show-icon
            title="EXE 替换会先检查并关闭完整路径匹配的运行中目标进程，然后替换目标文件。"
          />

          <el-alert
            v-if="selectedNode.fileType === 'script'"
            class="info-alert"
            type="info"
            :closable="false"
            show-icon
            title="BAT/CMD 支持查看、下载和运行；更新文件请在所属目录使用上传文件。"
          />

          <div class="button-row">
            <el-button
              v-if="selectedNode.isDirectory"
              type="warning"
              plain
              :icon="Upload"
              :loading="uploadLoading"
              @click="openDirectoryUploadPicker"
            >
              上传文件
            </el-button>
            <el-button
              v-if="!selectedNode.isDirectory && selectedNode.allowPreview"
              type="primary"
              plain
              :icon="View"
              :loading="previewLoading"
              @click="handlePreview"
            >
              查看
            </el-button>
            <el-button
              v-if="!selectedNode.isDirectory && selectedNode.allowDownload"
              type="success"
              plain
              :icon="Download"
              :loading="downloadLoading"
              @click="handleDownload"
            >
              下载
            </el-button>
            <el-button
              v-if="!selectedNode.isDirectory && selectedNode.allowRun"
              type="primary"
              plain
              :icon="VideoPlay"
              :loading="runLoading"
              @click="handleRunScript"
            >
              运行
            </el-button>
            <el-button
              v-if="!selectedNode.isDirectory && selectedNode.allowUpload"
              type="warning"
              plain
              :icon="Upload"
              :loading="replaceLoading"
              @click="openReplacePicker"
            >
              上传替换
            </el-button>
            <el-button
              v-if="!selectedNode.isDirectory"
              type="danger"
              plain
              :icon="Delete"
              :loading="deleteLoading"
              @click="handleDeleteFile"
            >
              删除
            </el-button>
          </div>

          <el-alert
            v-if="lastReplaceResult"
            class="result-alert"
            :type="lastReplaceResult.ok ? 'success' : 'error'"
            :closable="false"
            show-icon
          >
            <template #title>
              {{ lastReplaceResult.message }}
            </template>
          </el-alert>

          <dl v-if="lastReplaceDetail" class="replace-grid">
            <div>
              <dt>备份文件</dt>
              <dd>{{ lastReplaceDetail.backupPath || '-' }}</dd>
            </div>
            <div>
              <dt>替换阶段</dt>
              <dd>{{ lastReplaceDetail.phase || '-' }}</dd>
            </div>
            <div>
              <dt>进程关闭</dt>
              <dd>{{ processResultText(lastReplaceDetail) }}</dd>
            </div>
          </dl>

          <el-input
            v-if="previewVisible"
            v-model="previewContent"
            class="preview-box"
            type="textarea"
            :autosize="{ minRows: 12, maxRows: 24 }"
            readonly
          />
        </template>
      </section>
    </main>

    <el-upload
      ref="uploadRef"
      class="hidden-upload"
      :auto-upload="false"
      :show-file-list="false"
      :on-change="handleUploadChange"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  Delete,
  Document,
  Download,
  FolderOpened,
  Refresh,
  Upload,
  View,
  VideoPlay
} from '@element-plus/icons-vue';
import {
  deleteSgmFile,
  downloadSgmFile,
  getSgmFileTree,
  getSgmSnForbidden,
  getSgmSn27Counts,
  previewSgmFile,
  replaceSgmRuntimeFile,
  runSgmScript,
  uploadSgmFile,
  unbanSgmSn,
  updateSgmSn27Count
} from '../api/users';

const store = useStore();
const userData = computed(() => store.state.userData || {});
const models = computed(() => {
  const value = userData.value.responsibleModels;
  return Array.isArray(value) && value.length ? value : ['SGM'];
});

const treeProps = {
  label: 'name',
  children: 'children',
  isLeaf: 'leaf'
};

const fileTree = ref([]);
const selectedNode = ref(null);
const serverOptions = [
  { label: '国内服务器', value: 'CN Server', host: 'diagapi.cn.wwftool.top' },
  { label: '海外服务器', value: 'US Server', host: 'diagapi.us.wwftool.top' }
];
const selectedServer = ref(localStorage.getItem('sgmServer') || 'CN Server');
const activeSection = ref('files');
const newSnState = () => ({ sn: '', items: [], total: 0, page: 1, loading: false, loaded: false, error: '', filters: {} });
const forbiddenState = ref(newSnState());
const countState = ref(newSnState());
const currentSnState = computed(() => activeSection.value === 'counts' ? countState.value : forbiddenState.value);
const unbanLoading = ref(false);
const countEditVisible = ref(false);
const countSaving = ref(false);
const countEditError = ref('');
const countEditor = ref({});
const snBusy = computed(() => forbiddenState.value.loading || countState.value.loading || unbanLoading.value || countEditVisible.value || countSaving.value);

const formatSnDate = (date) => {
  const value = String(date || '');
  return /^\d{8}$/.test(value) ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}` : value || '-';
};

const querySnRecords = async (state, counts) => {
  if (state.loading) return;
  state.loading = true;
  state.error = '';
  let retryPage = false;
  try {
    const request = counts ? getSgmSn27Counts : getSgmSnForbidden;
    const response = await request({ ...state.filters, page: state.page, page_size: 20 }, selectedServer.value);
    const data = response.data?.data || {};
    state.items = data.items || [];
    state.total = data.total || 0;
    state.loaded = true;
    if (!state.items.length && state.total && state.page > 1) {
      state.page = Math.ceil(state.total / 20);
      retryPage = true;
    }
  } catch (error) {
    state.items = [];
    state.total = 0;
    state.loaded = false;
    state.error = errorMessage(error, '查询 SGM 数据失败');
  } finally {
    state.loading = false;
  }
  if (retryPage) await querySnRecords(state, counts);
};

const loadCurrentSnRecords = () => querySnRecords(currentSnState.value, activeSection.value === 'counts');
const searchSnRecords = () => {
  const state = currentSnState.value;
  state.filters = { sn: state.sn.trim() };
  state.page = 1;
  return loadCurrentSnRecords();
};
const resetSnFilters = () => {
  currentSnState.value.sn = '';
  return searchSnRecords();
};
const handleSectionChange = () => {
  if (activeSection.value === 'files' && !fileTree.value.length) return loadTree();
  if (activeSection.value !== 'files' && !currentSnState.value.loaded) return searchSnRecords();
};
const snWriteMessage = (data, fallback) => {
  const local = data.localWrite?.message || fallback;
  return data.remoteSync?.skipped ? `${local}，未配置远端同步` : `${local}${data.remoteSync ? '，远端同步成功' : ''}`;
};
const handleUnbanSn = async (row) => {
  unbanLoading.value = true;
  try {
    await ElMessageBox.confirm(`确认解除 ${row.sn} 的当前封禁？历史日志和计数将保留。`, '解封 SN', {
      confirmButtonText: '解封', cancelButtonText: '取消', type: 'warning'
    });
  } catch {
    unbanLoading.value = false;
    return;
  }
  try {
    const response = await unbanSgmSn(row.sn, selectedServer.value);
    ElMessage.success(snWriteMessage(response.data?.data || {}, '当前封禁已解除'));
  } catch (error) {
    ElMessage.error(errorMessage(error, '解封失败'));
  } finally {
    await querySnRecords(forbiddenState.value, false);
    unbanLoading.value = false;
  }
};
const openCountEditor = (row) => {
  countEditor.value = { sn: row.sn, query_date: row.query_date, cnt: row.cnt ?? 0, expected_cnt: row.cnt };
  countEditError.value = '';
  countEditVisible.value = true;
};
const closeCountEditor = (done) => { if (!countSaving.value) done(); };
const saveSnCount = async () => {
  if (countSaving.value) return;
  if (!Number.isInteger(countEditor.value.cnt) || countEditor.value.cnt < 0 || countEditor.value.cnt > 2147483647) {
    countEditError.value = '新次数必须为 0 到 2147483647 的整数';
    return;
  }
  countSaving.value = true;
  countEditError.value = '';
  try {
    const response = await updateSgmSn27Count({ ...countEditor.value }, selectedServer.value);
    countEditVisible.value = false;
    ElMessage.success(snWriteMessage(response.data?.data || {}, '次数已修改'));
    await querySnRecords(countState.value, true);
  } catch (error) {
    countEditError.value = errorMessage(error, '修改次数失败');
    if (error?.data?.localWrite?.success || error?.response?.data?.data?.localWrite?.success) {
      countEditVisible.value = false;
      ElMessage.error(countEditError.value);
      await querySnRecords(countState.value, true);
    }
  } finally {
    countSaving.value = false;
  }
};
const previewContent = ref('');
const previewVisible = ref(false);
const lastReplaceResult = ref(null);
const lastReplaceDetail = ref(null);
const loading = ref(false);
const previewLoading = ref(false);
const downloadLoading = ref(false);
const replaceLoading = ref(false);
const uploadLoading = ref(false);
const runLoading = ref(false);
const deleteLoading = ref(false);
const uploadRef = ref(null);
const treeRef = ref(null);
const uploadMode = ref('replace');

const currentServer = computed(() => (
  serverOptions.find((item) => item.value === selectedServer.value) || serverOptions[0]
));

const flatFileCount = computed(() => {
  const walk = (nodes) => nodes.reduce((count, node) => {
    const childCount = Array.isArray(node.children) ? walk(node.children) : 0;
    return count + childCount + (node.isDirectory ? 0 : 1);
  }, 0);
  return walk(fileTree.value);
});

const formatSize = (size) => {
  const value = Number(size || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const fileTypeLabel = (type) => {
  if (type === 'exe') return 'EXE';
  if (type === 'script') return 'BAT/CMD';
  if (type === 'runtime') return '运行文件';
  if (type === 'text') return '文本';
  if (type === 'directory') return '目录';
  return type || '文件';
};

const fileTagType = (type) => {
  if (type === 'exe') return 'danger';
  if (type === 'script') return 'warning';
  if (type === 'runtime') return 'warning';
  return 'info';
};

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  return data?.error || data?.msg || error?.info || error?.message || fallback;
};

const processResultText = (detail) => {
  const process = detail?.process || {};
  if (!process.found) return '未检测到运行进程';
  if (process.killed) return `已关闭 PID: ${(process.killedPids || []).join(', ')}`;
  return '关闭失败';
};

const parentPathOf = (relativePath = '') => {
  const normalized = String(relativePath || '').replace(/\\/g, '/');
  const index = normalized.lastIndexOf('/');
  return index >= 0 ? normalized.slice(0, index) : '';
};

const refreshDirectory = async (relativePath = '') => {
  const response = await getSgmFileTree(relativePath, selectedServer.value);
  const children = response.data?.data?.tree || [];
  if (!relativePath) {
    fileTree.value = children;
  } else {
    treeRef.value?.updateKeyChildren?.(relativePath, children);
  }
  return children;
};

const loadTree = async () => {
  loading.value = true;
  try {
    await refreshDirectory('');
  } catch (error) {
    ElMessage.error(errorMessage(error, '获取SGM文件树失败'));
  } finally {
    loading.value = false;
  }
};

const handleServerChange = async () => {
  localStorage.setItem('sgmServer', selectedServer.value);
  selectedNode.value = null;
  previewContent.value = '';
  previewVisible.value = false;
  lastReplaceResult.value = null;
  lastReplaceDetail.value = null;
  fileTree.value = [];
  forbiddenState.value = newSnState();
  countState.value = newSnState();
  if (activeSection.value === 'files') await loadTree();
  else await searchSnRecords();
};

const loadTreeNode = async (node, resolve) => {
  if (node.level === 0) {
    resolve(fileTree.value);
    return;
  }
  const data = node.data || {};
  if (!data.isDirectory) {
    resolve([]);
    return;
  }
  try {
    const children = await refreshDirectory(data.relativePath || '');
    resolve(children);
  } catch (error) {
    ElMessage.error(errorMessage(error, '加载目录失败'));
    resolve([]);
  }
};

const handleNodeClick = (node) => {
  selectedNode.value = node;
  previewContent.value = '';
  previewVisible.value = false;
  lastReplaceResult.value = null;
  lastReplaceDetail.value = null;
};

const handlePreview = async () => {
  if (!selectedNode.value?.relativePath) return;
  previewLoading.value = true;
  try {
    const response = await previewSgmFile(selectedNode.value.relativePath, selectedServer.value);
    previewContent.value = String(response.data || '');
    previewVisible.value = true;
  } catch (error) {
    ElMessage.error(errorMessage(error, '查看文件失败'));
  } finally {
    previewLoading.value = false;
  }
};

const handleDownload = async () => {
  if (!selectedNode.value?.relativePath) return;
  downloadLoading.value = true;
  try {
    await downloadSgmFile(selectedNode.value.relativePath, selectedServer.value);
    ElMessage.success('下载已开始');
  } catch (error) {
    ElMessage.error(errorMessage(error, '下载文件失败'));
  } finally {
    downloadLoading.value = false;
  }
};

const uploadConfirmText = () => {
  if (selectedNode.value?.fileType === 'exe') {
    return `该操作会先关闭当前正在运行的目标 EXE，只关闭完整路径匹配 ${selectedNode.value.relativePath} 的进程，然后替换目标文件。是否继续？`;
  }
  return `确认上传并替换 ${selectedNode.value.relativePath}？替换前后端会自动备份原文件。`;
};

const openReplacePicker = async () => {
  if (!selectedNode.value?.relativePath) return;
  try {
    uploadMode.value = 'replace';
    await ElMessageBox.confirm(uploadConfirmText(), '上传替换', {
      confirmButtonText: '继续',
      cancelButtonText: '取消',
      type: selectedNode.value.fileType === 'exe' ? 'warning' : 'info'
    });
    uploadRef.value?.$el?.querySelector('input[type="file"]')?.click();
  } catch {
    // 用户取消
  }
};

const openDirectoryUploadPicker = async () => {
  if (!selectedNode.value?.isDirectory) return;
  try {
    uploadMode.value = 'upload';
    await ElMessageBox.confirm(`确认上传文件到 ${selectedNode.value.relativePath || 'C:\\sgm'}？支持任意文件类型，同名文件将被覆盖。`, '上传文件', {
      confirmButtonText: '继续',
      cancelButtonText: '取消',
      type: 'warning'
    });
    uploadRef.value?.$el?.querySelector('input[type="file"]')?.click();
  } catch {
    // 用户取消
  }
};

const replaceMessage = (data, remote) => {
  if (data.localReplace?.success === true && remote.success !== false) {
    return `替换成功，本机已更新${remote.skipped ? '，未配置远端同步' : '，远端同步成功'}`;
  }
  return `替换或同步失败：${remote.message || data.localReplace?.message || '请查看后端日志'}`;
};

const handleUploadChange = async (uploadFile) => {
  const rawFile = uploadFile?.raw;
  if (!rawFile || !selectedNode.value?.relativePath) return;

  if (uploadMode.value === 'upload') {
    uploadLoading.value = true;
    try {
      await uploadSgmFile(selectedNode.value.relativePath, rawFile, selectedServer.value);
      ElMessage.success('上传成功');
      await refreshDirectory(selectedNode.value.relativePath || '');
    } catch (error) {
      ElMessage.error(errorMessage(error, '上传失败'));
    } finally {
      uploadLoading.value = false;
      uploadRef.value?.clearFiles?.();
    }
    return;
  }

  replaceLoading.value = true;
  lastReplaceResult.value = null;
  lastReplaceDetail.value = null;
  try {
    const response = await replaceSgmRuntimeFile(selectedNode.value.relativePath, rawFile, selectedServer.value);
    const data = response.data?.data || {};
    const remote = data.remoteSync || {};
    const ok = data.localReplace?.success === true && remote.success !== false;
    const message = replaceMessage(data, remote);

    lastReplaceDetail.value = data;
    lastReplaceResult.value = { ok, message };
    if (ok) {
      ElMessage.success(message);
    } else {
      ElMessage.error(message);
    }
    await refreshDirectory(parentPathOf(selectedNode.value.relativePath));
  } catch (error) {
    lastReplaceResult.value = {
      ok: false,
      message: errorMessage(error, '上传替换失败')
    };
    ElMessage.error(lastReplaceResult.value.message);
  } finally {
    replaceLoading.value = false;
    uploadRef.value?.clearFiles?.();
  }
};

const handleRunScript = async () => {
  if (!selectedNode.value?.relativePath) return;
  runLoading.value = true;
  try {
    const response = await runSgmScript(selectedNode.value.relativePath, selectedServer.value);
    const data = response.data?.data || {};
    ElMessage.success(`运行完成，退出码 ${data.exitCode ?? 0}`);
  } catch (error) {
    ElMessage.error(errorMessage(error, '运行失败'));
  } finally {
    runLoading.value = false;
  }
};

const handleDeleteFile = async () => {
  if (!selectedNode.value?.relativePath || selectedNode.value?.isDirectory) return;
  const targetPath = selectedNode.value.relativePath;
  try {
    await ElMessageBox.confirm(`确认删除 ${targetPath}？该操作不会自动备份。`, '删除文件', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    });
  } catch {
    return;
  }

  deleteLoading.value = true;
  try {
    await deleteSgmFile(targetPath, selectedServer.value);
    ElMessage.success('删除成功');
    const parentPath = parentPathOf(targetPath);
    selectedNode.value = null;
    previewContent.value = '';
    previewVisible.value = false;
    lastReplaceResult.value = null;
    lastReplaceDetail.value = null;
    await refreshDirectory(parentPath);
  } catch (error) {
    ElMessage.error(errorMessage(error, '删除失败'));
  } finally {
    deleteLoading.value = false;
  }
};

onMounted(loadTree);
</script>

<style scoped>
.sgm-page {
  min-height: 100%;
  min-width: 0;
  padding: 22px 26px 32px;
  box-sizing: border-box;
  background: #f6f8fb;
}

.sgm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid #dce8e1;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(31, 56, 88, 0.06);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.server-switch {
  flex: 1;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 24px;
}

.server-switch-title {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.server-switch-host {
  color: #334155;
  font-size: 12px;
  line-height: 1.4;
}

.eyebrow {
  margin-bottom: 6px;
  color: #047857;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.sgm-header h2,
.detail-head h3 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.sgm-header h2 {
  font-size: 22px;
}

.detail-head h3 {
  font-size: 20px;
}

.sgm-header p,
.detail-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-all;
}

.model-band {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0;
}

.model-chip {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  padding: 0 12px;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  background: #f0fdf4;
  color: #166534;
  font-size: 13px;
  font-weight: 700;
}

.sgm-workspace {
  display: grid;
  grid-template-columns: minmax(260px, 34%) minmax(0, 1fr);
  gap: 16px;
}

.tree-pane,
.detail-pane {
  min-width: 0;
  min-height: 560px;
  padding: 16px;
  border: 1px solid #dbe4ec;
  border-radius: 8px;
  background: #ffffff;
}

.pane-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #334155;
}

.pane-title span {
  color: #64748b;
  font-size: 12px;
}

.tree-scroll {
  height: 510px;
}

.tree-node {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-size: 13px;
}

.tree-node.active {
  color: #047857;
  font-weight: 700;
}

.tree-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.meta-grid,
.replace-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
}

.meta-grid div,
.replace-grid div {
  min-height: 72px;
  display: grid;
  align-content: center;
  gap: 6px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.meta-grid dt,
.replace-grid dt {
  color: #64748b;
  font-size: 12px;
}

.meta-grid dd,
.replace-grid dd {
  margin: 0;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  word-break: break-word;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.info-alert,
.result-alert {
  margin-top: 14px;
}

.preview-box {
  margin-top: 16px;
}

.hidden-upload {
  display: none;
}

.section-switch {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.sn-panel {
  min-width: 0;
  padding: 20px;
  border: 1px solid #dbe4ec;
  border-radius: 8px;
  background: #ffffff;
}

.sn-filters :deep(.el-input) {
  width: 240px;
  max-width: 100%;
}

.sn-error {
  margin-bottom: 16px;
}

.sn-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

:deep(.sn-count-dialog) {
  max-width: calc(100vw - 32px);
}

@media (max-width: 900px) {
  .sgm-page {
    padding: 16px;
  }

  .sgm-header,
  .detail-head {
    flex-direction: column;
  }

  .server-switch {
    width: 100%;
    min-width: 0;
    align-items: flex-start;
    padding: 0;
  }

  .sgm-workspace {
    grid-template-columns: 1fr;
  }

  .tree-pane,
  .detail-pane {
    min-height: 360px;
  }

  .tree-scroll {
    height: 330px;
  }

  .meta-grid,
  .replace-grid {
    grid-template-columns: 1fr;
  }
}
</style>
